import { ParsedUrlQuery } from 'querystring';

import { useMemo, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import FiltersBlock, {
  FiltersBlockProps,
} from '@components/ui/filters/FiltersBlock';
import PriceRangeFilter from '@components/ui/filters/PriceRangeFilter/PriceRangeFilter';
import NameFilter from '@components/ui/filters/NameFilter/NameFilter';
import SingleSelectionFilter from '@components/ui/filters/SingleSelectionFilter/SingleSelectionFilter';
import CollapsibleMultiSelectionFilter from '@components/ui/filters/MultiSelectionFilter/CollapsibleMultiSelectionFilter';
import {
  StyledSingleSelectionFilterWrapper,
  StyledMultiSelectionFilterWrapper,
  StyledPriceRangeFilterWrapper,
  StyledNameFilterWrapper,
} from '@components/ui/filters/FiltersBlock/ListingFiltersStyle';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import { FlexibleDiv } from '@components/formStyles';
import { isEmptyFilters, paramsToObject } from '@components/views/utils';
import SegmentsFilter from '@components/ui/filters/TourSegmentsFilter';
import CollapsibleMultiSelectionFilterWithSearchBar from '@components/ui/filters/MultiSelectionFilterWithSearchBar/CollapsibleMultiSelectionFilter';
import RegionFilter from '@components/views/components/RegionFilterWithSearchBar';
import NonCollapsibleMultiSelectionFilter from '@components/ui/filters/MultiSelectionFilterNonCollapsible/NonCollapsibleMultiSelectionFilter';

type TourFiltersProps = Pick<FiltersBlockProps, 'applicableFilters'> & {
  count?: number;
  isLoadingFilters?: boolean;
  itemText?: string;
  setIsRedirecting?: (value: boolean) => void;
  isSegmentPage?: boolean;
};

const TourFilters = ({
  applicableFilters,
  count,
  isLoadingFilters,
  itemText,
  setIsRedirecting,
  isSegmentPage,
}: TourFiltersProps) => {
  const searchParams = useSearchParams();
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  return (
    <FiltersBlock
      applicableFilters={applicableFilters}
      basePath=''
      setIsRedirecting={setIsRedirecting}
      setCheckedKeys={setCheckedKeys}
    >
      {({
        filters,
        onRegionChecked: onCheck,
        onlyAvailableHotels,
        handleSingleSelectionFilter,
        freeCancellation,
        keywords,
        setKeywords,
        handleKeywordsFilter,
        handleClearKeywordsFilter,
        minAccPriceValue,
        maxAccPriceValue,
        setMaxAccPriceValue,
        setMinAccPriceValue,
        handlePriceFilter,
        themes,
        handleSelectedThemes,
        departures,
        handleSelectedDepartures,
        transportations,
        handleSelectedTransportations,
        specialPeriods,
        handleSelectedSpecialPeriods,
        visas,
        handleSelectedVisas,
        regions,
        filtersData,
        segment,
        handleClearAllFilters,
        handleSelectedDates,
        handleSelectedSegments,
      }) => {
        return (
          <>
            <FlexibleDiv bottom={20}>
              <ClearAllFilters
                onClick={handleClearAllFilters}
                disabled={isLoadingFilters || isEmptyFilters(query)}
              />
            </FlexibleDiv>
            {(regions?.length ?? 0) < 1 ? null : (
              <FlexibleDiv bottom={20}>
                <RegionFilter
                  regions={regions}
                  onCheck={(checkedKeysValue) => {
                    setCheckedKeys(checkedKeysValue);
                    return onCheck(checkedKeysValue);
                  }}
                  selectedRegions={
                    filters.selectedRegions
                      ? filters.selectedRegions
                      : checkedKeys
                  }
                  totalItemCount={count ?? 0}
                  isLoading={!!isLoadingFilters}
                  title={itemText || ''}
                />
              </FlexibleDiv>
            )}

            {onlyAvailableHotels?.count ? (
              <StyledSingleSelectionFilterWrapper>
                <SingleSelectionFilter
                  title='Sadece yer olan turları göster'
                  subTitle={onlyAvailableHotels?.count || undefined}
                  checked={!!filters.onlyAvailableProperties}
                  mode='controlled'
                  name='status'
                  handleCheckBoxChange={(e) =>
                    handleSingleSelectionFilter(e, 'status')
                  }
                />
              </StyledSingleSelectionFilterWrapper>
            ) : null}

            {segment && segment.length > 0 && !isSegmentPage ? (
              <StyledMultiSelectionFilterWrapper>
                <SegmentsFilter
                  title='Ürün Kategorileri'
                  data={segment}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedSegments?.map((item) => item.key) ?? []
                  }
                  handleSelectedItems={handleSelectedSegments}
                />
              </StyledMultiSelectionFilterWrapper>
            ) : null}

            {freeCancellation?.count ? (
              <StyledSingleSelectionFilterWrapper>
                <SingleSelectionFilter
                  title={freeCancellation.value || 'Ücretsiz İptal'}
                  subTitle={freeCancellation?.count || undefined}
                  checked={!!filters.freeCancellation}
                  mode='controlled'
                  name='refundable'
                  handleCheckBoxChange={(e) =>
                    handleSingleSelectionFilter(e, 'refundable')
                  }
                />
              </StyledSingleSelectionFilterWrapper>
            ) : null}
            <StyledNameFilterWrapper>
              <NameFilter
                title='Tur Adı'
                name={keywords}
                setName={setKeywords}
                placeholder='Tur adı ile arayın'
                handleSearch={handleKeywordsFilter}
                handleClearKeywordsFilter={handleClearKeywordsFilter}
                withClear
              />
            </StyledNameFilterWrapper>

            <StyledPriceRangeFilterWrapper>
              <PriceRangeFilter
                minValue={minAccPriceValue}
                maxValue={maxAccPriceValue}
                setMinValue={setMinAccPriceValue}
                setMaxValue={setMaxAccPriceValue}
                title='Konaklama Fiyatı'
                handleSearch={handlePriceFilter}
              />
            </StyledPriceRangeFilterWrapper>

            {!transportations || transportations.length === 0 ? null : (
              <StyledMultiSelectionFilterWrapper>
                <NonCollapsibleMultiSelectionFilter
                  title='Ulaşım Tipi'
                  data={transportations}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedTransportationItems?.map(
                      (item) => item.key
                    ) ?? []
                  }
                  handleSelectedItems={handleSelectedTransportations}
                />
              </StyledMultiSelectionFilterWrapper>
            )}

            {!visas || visas.length === 0 ? null : (
              <StyledMultiSelectionFilterWrapper>
                <NonCollapsibleMultiSelectionFilter
                  title='Vize Tipi'
                  data={visas}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedVisaItems?.map((item) => item.key) ?? []
                  }
                  handleSelectedItems={handleSelectedVisas}
                />
              </StyledMultiSelectionFilterWrapper>
            )}

            {!specialPeriods || specialPeriods.length === 0 ? null : (
              <StyledMultiSelectionFilterWrapper>
                <CollapsibleMultiSelectionFilterWithSearchBar
                  title='Özel Dönem'
                  data={specialPeriods}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedSpecialPeriodItems?.map(
                      (item) => item.key
                    ) ?? []
                  }
                  handleSelectedItems={handleSelectedSpecialPeriods}
                />
              </StyledMultiSelectionFilterWrapper>
            )}

            {!themes || themes.length === 0 ? null : (
              <StyledMultiSelectionFilterWrapper>
                <CollapsibleMultiSelectionFilterWithSearchBar
                  title='Tur Temaları'
                  data={themes}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedThemeItems?.map((item) => item.key) ?? []
                  }
                  handleSelectedItems={handleSelectedThemes}
                />
              </StyledMultiSelectionFilterWrapper>
            )}

            {filtersData?.length > 0 ? (
              <StyledMultiSelectionFilterWrapper>
                <CollapsibleMultiSelectionFilter
                  title='Tur Kalkış Tarihleri'
                  subTitle={filters?.selectedDateItems?.length}
                  showSelectedItemsCount={Boolean(
                    filters?.selectedDateItems?.length
                  )}
                  data={filtersData}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedDateItems?.map((item) => item.key) ?? []
                  }
                  handleSelectedItems={handleSelectedDates}
                />
              </StyledMultiSelectionFilterWrapper>
            ) : null}

            {!departures || departures.length === 0 ? null : (
              <StyledMultiSelectionFilterWrapper>
                <CollapsibleMultiSelectionFilter
                  title='Çıkış Noktası'
                  data={departures}
                  subTitleKey='count'
                  selectedItems={
                    filters.selectedDepartureItems?.map((item) => item.key) ??
                    []
                  }
                  handleSelectedItems={handleSelectedDepartures}
                />
              </StyledMultiSelectionFilterWrapper>
            )}
          </>
        );
      }}
    </FiltersBlock>
  );
};

export default TourFilters;
